import { InventoryDashboardRecentActivityRow } from '@/renderer/types/inventory';
import React from 'react';
import {
  formatCompactCurrency,
  formatDateTime,
  summarizeRecentActivity,
} from './inventoryDashboard';
import {
  getRecentActivityLabel,
  getRecentActivitySummaryTone,
  getRecentActivityTone,
} from '../movements/movementPresentation';

interface InventoryDashboardRecentActivityCardProps {
  rows: InventoryDashboardRecentActivityRow[];
}

export function InventoryDashboardRecentActivityCard({
  rows,
}: InventoryDashboardRecentActivityCardProps) {
  const summaries = summarizeRecentActivity(rows).slice(0, 4);
  const latestRows = rows.slice(0, 4);
  const summaryCards = summaries.map((summary) => {
    const tone = getRecentActivitySummaryTone(summary.key);

    return (
      <div
        key={summary.key}
        className="rounded-xl border border-gray-100 bg-slate-50 px-3.5 py-2.5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${tone.badgeClassName}`}
              >
                {summary.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {summary.count.toLocaleString()} entries • last at{' '}
              {formatDateTime(summary.latestOccurredAt)}
            </p>
          </div>
          <span className={`text-sm font-semibold ${tone.quantityClassName}`}>
            {formatCompactCurrency(summary.totalAmount)}
          </span>
        </div>
      </div>
    );
  });

  return (
    <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
      <div>
        <p className="text-sm font-semibold text-slate-900">Recent activity</p>
        <p className="mt-1 text-xs text-slate-500">
          A quick pulse on what inventory-related work has happened in the
          selected period.
        </p>
      </div>

      {summaries.length ? (
        <div className="mt-3 space-y-2.5">
          {summaryCards}

          <div className="rounded-xl border border-gray-100">
            <div className="border-b border-gray-100 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Latest records
            </div>
            <div className="max-h-[220px] overflow-y-auto">
              {latestRows.map((row) => {
                const tone = getRecentActivityTone(row);

                return (
                  <div
                    key={`${row.activity_type}-${row.activity_id}`}
                    className="border-b border-gray-100 px-4 py-3 last:border-b-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${tone.badgeClassName}`}
                          >
                            {getRecentActivityLabel(row)}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-sm font-medium text-slate-900">
                          {row.headline}
                        </p>
                        {row.detail ? (
                          <p className="truncate text-xs text-slate-500">
                            {row.detail}
                          </p>
                        ) : null}
                        <p className="mt-1 text-[11px] text-slate-400">
                          {formatDateTime(row.occurred_at)}
                        </p>
                      </div>
                      <span
                        className={`whitespace-nowrap text-sm font-semibold ${tone.quantityClassName}`}
                      >
                        {formatCompactCurrency(Math.abs(row.amount ?? 0))}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          No inventory activity was recorded in this period.
        </div>
      )}
    </div>
  );
}
